import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, combineLatest, map, Observable, startWith } from 'rxjs';

@Component({
  selector: 'app-autocomplete-input',
  standalone: true,
  imports: [CommonModule, MatAutocompleteModule, MatInputModule, MatFormFieldModule, MatTooltipModule, ReactiveFormsModule],
  templateUrl: './autocomplete-input.component.html',
  styleUrls: ['./autocomplete-input.component.scss']
})
export class AutocompleteInputComponent implements OnInit, OnChanges {
  @Input() options: string[] = [];
  @Input() placeholder: string = '';
  @Input() isPlain = false;
  @Input() value: string = '';
  @Input() required = false;
  @Input() disabled = false;
  @Output() optionSelected = new EventEmitter<string>();

  form = new FormControl('');
  filteredOptions!: Observable<string[]>;
  private readonly options$ = new BehaviorSubject<string[]>([]);

  ngOnInit() {
    this.options$.next(this.normalizeOptions(this.options));

    this.filteredOptions = combineLatest([
      this.form.valueChanges.pipe(startWith('')),
      this.options$,
    ]).pipe(
      map(([value]) => this._filter(value || ''))
    );

    if (this.required && !this.disabled) {
      this.form.addValidators(Validators.required);
      this.form.updateValueAndValidity({ emitEvent: false });
    }

    this.syncDisabledState();

    this.form.valueChanges.subscribe((value: any) => {
      this.optionSelected.emit(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["options"]) {
      this.options$.next(this.normalizeOptions(this.options));
    }
    if (changes["value"] && changes["value"].currentValue) {
      this.form.setValue(this.value);
    }
    if (changes["disabled"]) {
      this.syncDisabledState();
    }
  }

  onInputFocus(): void {
    if (this.disabled) return;
    this.form.setValue(this.form.value ?? '', { emitEvent: true });
  }

  private syncDisabledState(): void {
    if (this.disabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private normalizeOptions(options: string[] | null | undefined): string[] {
    return (options ?? []).map((option) => String(option));
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.options$.value.filter((option) => option.toLowerCase().includes(filterValue));
  }
}
